/**
 * WaterCard — compact hydration counter usable on Food Log + dashboard widgets.
 *
 * Features:
 *  • Animated bottle that fills as the user drinks
 *  • One-tap +8/+16/+24 oz buttons + custom amount + undo
 *  • Streak chip showing consecutive goal-hit days
 *  • Editable daily goal (pencil opens a tiny dialog)
 *
 * All API calls go through axios with cookie credentials + the in-memory token
 * so this works the same on free and premium tiers.
 */
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Droplet, Plus, Undo2, Flame, Pencil, X, Loader2, ArrowRight, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const QUICK_ADDS = [
  { oz: 8, label: 'Glass' },
  { oz: 16, label: 'Bottle' },
  { oz: 24, label: 'Large' },
];

// Always compute the user's LOCAL date (not UTC) so the counter buckets
// entries by the user's calendar day. UTC-based dates caused the counter
// to "carry over" the previous day's total across midnight.
const localDateStr = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const WaterCard = ({ compact = false, showOverviewLink = false, date, headerSlot = null }) => {
  const { token } = useAuth();
  // If a date is passed in (e.g. from the FoodLog date strip), use it as the
  // controlling date so the prev/next arrows reset / shift the water totals
  // alongside the food entries. Otherwise fall back to the user's local today.
  const effectiveDate = date || localDateStr();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showGoal, setShowGoal] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [showEntries, setShowEntries] = useState(false);
  // The local calendar date the current `data` represents — used to detect
  // an across-midnight refresh so the bottle visually empties on day rollover.
  const [activeDate, setActiveDate] = useState(effectiveDate);

  const fetchDay = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/water-log/day`, {
        params: { date: effectiveDate },
        withCredentials: true,
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      setData(res.data);
      setActiveDate(effectiveDate);
    } catch {
      /* silent — keep last data */
    }
    setLoading(false);
  }, [token, effectiveDate]);

  // Refetch whenever the controlling date changes (e.g. user taps prev/next on
  // the FoodLog date strip).
  useEffect(() => { fetchDay(); }, [fetchDay]);

  // Re-fetch when the tab regains focus OR the local date changes — so the
  // counter naturally resets at midnight without the user reloading.
  // Also handles mobile Safari/Chrome BFCache.
  useEffect(() => {
    // Skip the auto-rollover poll when a parent is controlling the date — the
    // parent's date strip is the source of truth in that case.
    if (date) return undefined;
    const refreshIfStale = () => {
      const today = localDateStr();
      if (today !== activeDate) {
        fetchDay();
      }
    };
    const onPageShow = (event) => {
      if (event.persisted) {
        fetchDay();
      } else {
        refreshIfStale();
      }
    };
    const onVisibility = () => { if (!document.hidden) refreshIfStale(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', refreshIfStale);
    window.addEventListener('pageshow', onPageShow);
    const interval = setInterval(refreshIfStale, 60_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', refreshIfStale);
      window.removeEventListener('pageshow', onPageShow);
      clearInterval(interval);
    };
  }, [activeDate, fetchDay, date]);

  const addOunces = async (oz, label) => {
    setAdding(true);
    try {
      await axios.post(`${BACKEND}/api/water-log/add`,
        { ounces: oz, label, log_date: effectiveDate },
        { withCredentials: true, headers: { Authorization: token ? `Bearer ${token}` : undefined } });
      await fetchDay();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log');
    }
    setAdding(false);
  };

  const undo = async () => {
    setAdding(true);
    try {
      const res = await axios.post(`${BACKEND}/api/water-log/undo`,
        { log_date: effectiveDate },
        { withCredentials: true, headers: { Authorization: token ? `Bearer ${token}` : undefined } });
      toast.success(`Removed ${Math.round(res.data.deleted.ounces)} oz`);
      await fetchDay();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nothing to undo');
    }
    setAdding(false);
  };

  const deleteEntry = async (entryId) => {
    setAdding(true);
    try {
      await axios.delete(`${BACKEND}/api/water-log/entries/${entryId}`, {
        withCredentials: true,
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      toast.success('Entry removed');
      await fetchDay();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
    setAdding(false);
  };

  const pct = data?.pct || 0;
  const total = data?.total_oz || 0;
  const goal = data?.goal_oz || 64;
  const streak = data?.streak_days || 0;
  const hitGoal = total >= goal;

  return (
    <div
      className={`bg-card border border-border rounded-2xl ${compact ? 'p-4' : 'p-5'} relative overflow-hidden`}
      data-testid="water-card"
    >
      {/* Decorative blue accent in the background */}
      <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

      {/* Optional header (e.g. parent's date strip) — visually merges into the
          card so users see one cohesive panel instead of two stacked boxes. */}
      {headerSlot && (
        <div className="mb-4 -mx-4 -mt-4 sm:-mx-5 sm:-mt-5 px-4 py-3 sm:px-5 border-b border-border/60 bg-black/20">
          {headerSlot}
        </div>
      )}

      <div className="flex items-start gap-4">
        {/* Animated bottle */}
        <div className="relative h-24 w-12 sm:h-28 sm:w-14 shrink-0" data-testid="water-bottle">
          {/* bottle outline */}
          <div className="absolute inset-0 rounded-b-2xl rounded-t-md border-2 border-blue-400/60 overflow-hidden bg-black/40">
            {/* water fill */}
            <div
              className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-500 to-blue-300 transition-[height] duration-700 ease-out"
              style={{ height: `${Math.min(100, pct)}%` }}
              data-testid="water-fill"
            />
            {/* shimmer line on top of water */}
            <div
              className="absolute left-0 right-0 h-0.5 bg-white/40 transition-[bottom] duration-700"
              style={{ bottom: `${Math.min(100, pct)}%` }}
            />
          </div>
          {/* bottle neck */}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 h-2 w-5 rounded-t-md border-2 border-b-0 border-blue-400/60 bg-card" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-heading text-sm sm:text-base text-white uppercase tracking-wider flex items-center gap-1.5">
              <Droplet className="h-4 w-4 text-blue-400" /> Water
            </h3>
            <div className="flex items-center gap-2">
              {streak > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/30"
                  data-testid="water-streak"
                >
                  <Flame className="h-3 w-3" /> {streak} day{streak !== 1 ? 's' : ''}
                </span>
              )}
              {showOverviewLink && (
                <Link
                  to="/water"
                  className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
                  data-testid="water-overview-link"
                >
                  Overview <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </div>
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="font-heading text-2xl sm:text-3xl text-white" data-testid="water-total">{Math.round(total)}</span>
            <span className="text-muted-foreground text-xs">/ {Math.round(goal)} oz</span>
            <button
              onClick={() => setShowGoal(true)}
              className="ml-1 text-muted-foreground hover:text-primary"
              aria-label="Edit daily goal"
              data-testid="water-edit-goal"
            >
              <Pencil className="h-3 w-3" />
            </button>
          </div>
          {/* Progress bar */}
          <div className="mt-2 h-2 rounded-full bg-white/5 overflow-hidden">
            <div
              className={`h-full transition-[width] duration-700 ease-out ${hitGoal ? 'bg-green-400' : 'bg-blue-400'}`}
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>

          {/* Quick-add buttons */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {QUICK_ADDS.map((q) => (
              <button
                key={q.oz}
                onClick={() => addOunces(q.oz, q.label)}
                disabled={adding || loading}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-md disabled:opacity-50 transition-colors"
                data-testid={`water-add-${q.oz}`}
              >
                <Plus className="h-3 w-3" /> {q.oz}oz
              </button>
            ))}
            <button
              onClick={() => setShowCustom(true)}
              disabled={adding || loading}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-xs rounded-md transition-colors"
              data-testid="water-add-custom"
            >
              Custom
            </button>
            <button
              onClick={undo}
              disabled={adding || loading || total <= 0}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-muted-foreground hover:text-white text-xs rounded-md disabled:opacity-40"
              aria-label="Undo last add"
              data-testid="water-undo"
            >
              <Undo2 className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* View / clean up today's entries — handy for spotting mis-bucketed
              legacy entries from before the local-date fix. */}
          {(data?.entries?.length || 0) > 0 && (
            <button
              type="button"
              onClick={() => setShowEntries((s) => !s)}
              className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-white"
              data-testid="water-toggle-entries"
            >
              {showEntries ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showEntries ? 'Hide' : 'View'} today&apos;s entries ({data.entries.length})
            </button>
          )}
          {showEntries && (data?.entries || []).length > 0 && (
            <div className="mt-2 space-y-1.5 border-t border-border/40 pt-2" data-testid="water-entries-list">
              {data.entries.map((e) => {
                const ts = e.created_at ? new Date(e.created_at) : null;
                const timeStr = ts ? ts.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '—';
                return (
                  <div key={e.id} className="flex items-center justify-between gap-2 text-xs bg-black/30 rounded-md px-2 py-1" data-testid={`water-entry-${e.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Droplet className="h-3 w-3 text-blue-400 shrink-0" />
                      <span className="text-white shrink-0">{Math.round(e.ounces)} oz</span>
                      <span className="text-muted-foreground truncate">· {e.label || 'water'} · {timeStr}</span>
                    </div>
                    <button
                      onClick={() => deleteEntry(e.id)}
                      disabled={adding}
                      className="text-muted-foreground hover:text-red-400 shrink-0"
                      aria-label="Delete entry"
                      data-testid={`water-entry-delete-${e.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showGoal && (
        <GoalDialog
          current={data?.goal_oz || 64}
          source={data?.goal_source || 'default'}
          onClose={() => setShowGoal(false)}
          onSaved={async () => { setShowGoal(false); await fetchDay(); }}
        />
      )}
      {showCustom && (
        <CustomAmountDialog
          onClose={() => setShowCustom(false)}
          onAdd={async (oz) => { await addOunces(oz, 'Custom'); setShowCustom(false); }}
        />
      )}
    </div>
  );
};

const GoalDialog = ({ current, source, onClose, onSaved }) => {
  const { token } = useAuth();
  const [val, setVal] = useState(String(Math.round(current)));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const n = parseFloat(val);
    if (Number.isNaN(n) || n < 0 || n > 400) {
      toast.error('Goal must be 0–400 oz');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${BACKEND}/api/water-log/goal`,
        { daily_goal_oz: n },
        { withCredentials: true, headers: { Authorization: token ? `Bearer ${token}` : undefined } });
      toast.success(n === 0 ? 'Goal reset to auto' : `Goal set to ${n} oz`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose} data-testid="water-goal-dialog">
      <div onClick={(e) => e.stopPropagation()} className="bg-card border-2 border-blue-500/40 rounded-2xl max-w-sm w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base text-white uppercase tracking-wider">Daily Water Goal</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Currently {source === 'weight' ? 'auto-set from your body weight' : source === 'default' ? 'using the 64 oz default' : 'a custom value you set'}.
        </p>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="e.g. 96"
            className="bg-black/40 border-border text-white"
            data-testid="water-goal-input"
          />
          <span className="text-muted-foreground text-sm">oz</span>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">Tip: set to 0 to auto-calc from your body weight (½ oz per lb).</p>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white" data-testid="water-goal-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save
          </Button>
        </div>
      </div>
    </div>
  );
};

const CustomAmountDialog = ({ onClose, onAdd }) => {
  const [oz, setOz] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose} data-testid="water-custom-dialog">
      <div onClick={(e) => e.stopPropagation()} className="bg-card border-2 border-blue-500/40 rounded-2xl max-w-sm w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base text-white uppercase tracking-wider">Custom Amount</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            inputMode="decimal"
            value={oz}
            onChange={(e) => setOz(e.target.value)}
            placeholder="oz"
            className="bg-black/40 border-border text-white"
            data-testid="water-custom-input"
          />
          <span className="text-muted-foreground text-sm">oz</span>
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={busy}>Cancel</Button>
          <Button
            onClick={async () => {
              const n = parseFloat(oz);
              if (Number.isNaN(n) || n <= 0) { toast.error('Enter a positive number'); return; }
              setBusy(true);
              await onAdd(n);
              setBusy(false);
            }}
            disabled={busy}
            className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
            data-testid="water-custom-save"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-1" />}
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};
